/**
 * Validates a post-login redirect target carried on the `returnUrl` query parameter — e.g. the PA
 * site-visit notification email's login link (`SiteVisitEmailHandler.loginUrl`, connect-service).
 *
 * This value arrives over an untrusted channel: a URL is something a person can copy, forward, or a
 * phishing email can forge a lookalike of, so it must be validated before `login.component.ts` or
 * `app.routes.ts`'s `loginLandingGuard` ever hand it to the router. An unvalidated redirect target is
 * the textbook open-redirect bug — a login flow that blindly follows a caller-supplied destination to
 * whatever host that destination names.
 *
 * Accepted: a root-relative, same-origin path — exactly one leading `/`, no scheme, no host. Rejected
 * (returns `''`, the existing "no redirect" value `AppService`/`login.component.ts` already use):
 *  - empty / missing
 *  - anything not starting with `/` (a bare host, or a `scheme:` value like `javascript:...`)
 *  - `//host/...` — protocol-relative; a browser resolves this against its OWN scheme onto `host`,
 *    which is exactly an external redirect wearing a path-shaped disguise
 *  - `/\host/...` — some browsers normalize a leading backslash to `//`, so this is the same attack
 *  - anything containing `://` — a defensively-rejected embedded scheme
 */
import { Router } from '@angular/router';

export function sanitizeReturnUrl(raw: string | null | undefined): string {
  if (!raw) {
    return '';
  }
  const trimmed = raw.trim();
  if (trimmed === '' || trimmed[0] !== '/' || trimmed[1] === '/' || trimmed[1] === '\\') {
    return '';
  }
  if (trimmed.includes('://')) {
    return '';
  }
  return trimmed;
}

/** Where a redirect goes when the caller-supplied one is unusable. */
export const DEFAULT_RETURN_URL = '/dashboard';

/**
 * The redirect target a router can actually be handed, or `''` when there is none — the value
 * `loginLandingGuard` and `login.component.ts` both fall back to {@link DEFAULT_RETURN_URL} on.
 *
 * {@link sanitizeReturnUrl} answers whether a value is *allowed*; this answers whether it *survives
 * the parser*, which is a separate question and the one both call sites were getting wrong.
 * `/scheduling(x` is root-relative and same-origin, so it passes the sanitizer untouched — and then
 * Angular's URL grammar rejects it, because `parseParens` raises UNPARSABLE_URL on an unterminated
 * auxiliary-route group (`@angular/router` `router.mjs:704`; `capture` is a second throw site).
 *
 * 🔴 That rejection is **swallowed, not thrown**: `Router.parseUrl` (`router.mjs:5649`) wraps the
 * serializer in its own `try/catch` and answers `parse('/')`. So a crafted `returnUrl` does not error —
 * it silently redirects to the ROOT, which is not the documented fallback and not where either call
 * site says it sends a rejected value. The degraded root tree is therefore the signal to test for; the
 * `catch` below is real belt-and-braces rather than the mechanism, covering a custom `UrlSerializer`
 * or a future Angular that stops swallowing.
 *
 * Detecting the failure by its answer, rather than by screening `(` in the sanitizer, is deliberate:
 * `capture` is a different throw site from `parseParens` and the grammar can grow more, so a character
 * screen would only ever cover the productions someone thought of.
 */
export function resolveReturnUrl(
  router: Router,
  raw: string | null | undefined
): string {
  const safe = sanitizeReturnUrl(raw);
  if (!safe) {
    return '';
  }
  try {
    // Root is never a destination worth redirecting to here — `rootLandingGuard` only bounces straight
    // back out — so a value that parses to it is indistinguishable from one that failed to parse, and
    // both want the same answer.
    return router.serializeUrl(router.parseUrl(safe)) === '/' ? '' : safe;
  } catch {
    return '';
  }
}
